// Aug-2026 (user request) — surface the source PR's attachments (header +
// line-level) directly on the converted Project Ops PO so users can check
// them in the PO section without opening the PR. Read-only; opens via the
// authenticated blob endpoints. Hidden silently if the caller lacks the
// project_pr view permission or the PR has no files.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip } from "lucide-react";

export function PrAttachmentsStrip({ projectId, prId }) {
  const [pr, setPr] = useState(null);
  useEffect(() => {
    if (!projectId || !prId) return;
    api.get(`/projects/${projectId}/prs/${prId}`)
      .then((r) => setPr(r.data)).catch(() => setPr(null));
  }, [projectId, prId]);
  if (!pr) return null;
  const open = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (!w) { const a = document.createElement("a"); a.href = url; a.download = filename || "attachment"; a.click(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open the attachment"); }
  };
  const header = pr.attachments || [];
  const lineDocs = (pr.lines || []).filter((l) => l.attachment);
  if (!header.length && !lineDocs.length) return null;
  const chipCls = "text-sky-700 hover:underline inline-flex items-center gap-1 text-[11px] font-bold max-w-[240px]";
  return (
    <div className="mt-1.5 border border-sky-200 bg-sky-50/60 rounded px-2.5 py-1.5" data-testid="po-pr-attachments">
      <div className="text-[9px] uppercase tracking-wider font-bold text-sky-800 mb-1">
        PR Attachments · {header.length + lineDocs.length} file(s) — from {pr.pr_number}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {header.map((a) => (
          <button key={a.id} type="button" data-testid={`po-pr-att-${a.id}`}
            onClick={() => open(`/projects/${projectId}/prs/${prId}/attachments/${a.id}`, a.filename)}
            className={chipCls}
            title={`${a.filename} (${Math.max(1, Math.round((a.size || 0) / 1024))} KB)`}>
            <Paperclip size={11} /> <span className="truncate">{a.filename}</span>
          </button>
        ))}
        {lineDocs.map((l) => (
          <button key={l.line_uid} type="button" data-testid={`po-pr-line-att-${l.line_uid}`}
            onClick={() => open(`/projects/${projectId}/prs/${prId}/lines/${l.line_uid}/attachment`, l.attachment.filename)}
            className={chipCls}
            title={`Line: ${l.item_name || l.name || ""} — ${l.attachment.filename}`}>
            <Paperclip size={11} /> <span className="truncate">{l.attachment.filename}</span>
            <span className="text-slate-400 font-normal">(line)</span>
          </button>
        ))}
      </div>
    </div>
  );
}
